import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { API, useAuth } from "../App";
import WorkerLayout from "../components/WorkerLayout";
import { Card, CardContent } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { QrCode, LogIn, LogOut, Clock, MapPin } from "lucide-react";
import { toast } from "sonner";

export default function WorkerDashboard() {
  const [todayRecords, setTodayRecords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentStatus, setCurrentStatus] = useState(null);
  const navigate = useNavigate();
  const { user } = useAuth();

  useEffect(() => {
    fetchTodayRecords();
  }, []);

  const fetchTodayRecords = async () => {
    try {
      const response = await axios.get(`${API}/time-records/today`);
      setTodayRecords(response.data);
      
      // Determine current status
      if (response.data.length > 0) {
        const lastRecord = response.data[0];
        setCurrentStatus(lastRecord.type === "entry" ? "active" : "inactive");
      }
    } catch (error) {
      toast.error("Erro ao carregar registros");
    } finally {
      setLoading(false);
    }
  };

  const formatTime = (timestamp) => {
    return new Date(timestamp).toLocaleTimeString("pt-BR", {
      hour: "2-digit",
      minute: "2-digit"
    });
  };

  const getTotalHours = () => {
    let totalMinutes = 0;
    let entryTime = null;

    // Sort by timestamp ascending
    const sortedRecords = [...todayRecords].sort(
      (a, b) => new Date(a.timestamp) - new Date(b.timestamp)
    );

    for (const record of sortedRecords) {
      if (record.type === "entry") {
        entryTime = new Date(record.timestamp);
      } else if (record.type === "exit" && entryTime) {
        const exitTime = new Date(record.timestamp);
        totalMinutes += (exitTime - entryTime) / 60000;
        entryTime = null;
      }
    }

    // If still active, add time until now
    if (entryTime && currentStatus === "active") {
      totalMinutes += (new Date() - entryTime) / 60000;
    }

    const hours = Math.floor(totalMinutes / 60);
    const minutes = Math.round(totalMinutes % 60);
    return `${hours}h ${minutes}m`;
  };

  if (loading) {
    return (
      <WorkerLayout>
        <div className="flex items-center justify-center h-64">
          <div className="spinner"></div>
        </div>
      </WorkerLayout>
    );
  }

  return (
    <WorkerLayout>
      <div className="space-y-6 animate-fade-in" data-testid="worker-dashboard">
        {/* Header */}
        <div className="text-center pt-4">
          <h1 className="text-2xl font-bold text-gray-800 font-manrope">
            Olá, {user?.name}!
          </h1>
          <p className="text-gray-500 mt-1">
            {new Date().toLocaleDateString("pt-BR", { 
              weekday: "long", 
              day: "numeric", 
              month: "long" 
            })}
          </p>
        </div>

        {/* Status Card */}
        <Card className={`bg-white border-2 shadow-lg ${
          currentStatus === "active" ? "border-[#2ECC40]" : "border-gray-200"
        }`}>
          <CardContent className="p-6 text-center">
            <div className={`w-20 h-20 mx-auto rounded-full flex items-center justify-center mb-4 ${
              currentStatus === "active" 
                ? "bg-green-100" 
                : "bg-gray-100"
            }`}>
              <Clock className={`w-10 h-10 ${
                currentStatus === "active" ? "text-[#2ECC40]" : "text-gray-400"
              }`} />
            </div>
            <p className="text-lg font-semibold text-gray-800">
              {currentStatus === "active" ? "Você está trabalhando" : "Não registrado"}
            </p>
            {currentStatus === "active" && todayRecords[0] && (
              <p className="text-sm text-gray-500 mt-1">
                Entrada às {formatTime(todayRecords[0].timestamp)} em {todayRecords[0].location_name}
              </p>
            )}
            <p className="text-3xl font-bold gradient-text mt-4 font-manrope">
              {getTotalHours()}
            </p>
            <p className="text-xs text-gray-400">trabalhadas hoje</p>
          </CardContent>
        </Card>

        {/* Scan Button */}
        <Button
          onClick={() => navigate("/worker/scan")}
          className="w-full h-20 btn-primary text-white font-bold text-lg"
          data-testid="scan-qr-button"
        >
          <QrCode className="mr-3 h-8 w-8" />
          Escanear QR Code
        </Button>

        {/* Today's Records */}
        <div>
          <h2 className="text-lg font-semibold text-gray-800 mb-4">Registros de Hoje</h2>
          
          {todayRecords.length === 0 ? (
            <Card className="bg-white border border-gray-100 shadow-sm">
              <CardContent className="py-12 text-center">
                <QrCode className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                <p className="text-gray-500">Nenhum registro hoje</p>
                <p className="text-sm text-gray-400 mt-1">
                  Escaneie o QR code para registrar entrada
                </p>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-3">
              {todayRecords.map((record) => (
                <Card key={record.id} className="bg-white border border-gray-100 shadow-sm">
                  <CardContent className="p-4">
                    <div className="flex items-center gap-4">
                      <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                        record.type === "entry" 
                          ? "bg-green-100 text-[#2ECC40]" 
                          : "bg-amber-100 text-[#F5A623]"
                      }`}>
                        {record.type === "entry" ? <LogIn size={18} /> : <LogOut size={18} />}
                      </div>
                      <div className="flex-1">
                        <p className="font-medium text-gray-800">
                          {record.type === "entry" ? "Entrada" : "Saída"}
                        </p>
                        <p className="text-sm text-gray-500 flex items-center gap-1">
                          <MapPin size={12} />
                          {record.location_name}
                        </p>
                      </div>
                      <span className="text-lg font-mono text-gray-600">
                        {formatTime(record.timestamp)}
                      </span>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </div>
      </div>
    </WorkerLayout>
  );
}
